import { Spacing } from '@persona-id/design-tokens';
import { ChevronDownIcon, ChevronRightIcon } from '@persona-id/design-tokens/icons';
import { Fragment, VFC } from 'react';
import { Link } from 'react-router-dom-v5-compat';
import styled from 'styled-components';

import CaseStatusSticker from 'dashboard/components/CaseStatusSticker';
import InquiryStatusSticker from 'dashboard/components/InquiryStatusSticker';
import ReportStatusSticker from 'dashboard/components/ReportStatusSticker';
import VerificationStatusSticker from 'dashboard/components/VerificationStatusSticker';
import { TransactionWithRelatedData } from 'dashboard/routes/DashboardTransactionsShowRoute/util';
import { reportTypeDisplay } from 'dashboard/views/DashboardReportsView/ReportsTable';
import Badge from 'design-system/components/Badge';
import Card from 'design-system/components/Card';
import Spacer from 'design-system/components/Spacer';
import { Paragraph } from 'design-system/components/Text';
import useToggle from 'lib/hooks/useToggle';
import { intersperse } from 'lib/utils/array';

const CollapsibleHeader = styled.div`
  cursor: pointer;
`;

const TitleAndSticker = styled.div`
  display: flex;
  align-items: center;

  margin-top: ${Spacing[2]};

  ${CollapsibleHeader} + & {
    margin-top: ${Spacing[4]};
  }

  & > div:first-child {
    & > a {
      display: block;
    }
  }

  & > div:nth-child(2) {
    margin-left: ${Spacing[2]};
    flex-shrink: 0;
  }
`;

interface QuickLinksForTypeProps {
  type: string;
  links: {
    title: string;
    path: string;
    subtitle?: string;
    sticker?: JSX.Element;
  }[];
}

const QuickLinksForType: VFC<QuickLinksForTypeProps> = ({ type, links }) => {
  const [opened, { toggle }] = useToggle(true);
  return (
    <>
      <CollapsibleHeader id={`quick-links__header-${type}`} onClick={toggle}>
        <Card.SectionHeader>
          {opened ? <ChevronDownIcon /> : <ChevronRightIcon />}
          {type}
          <Badge>{links.length}</Badge>
        </Card.SectionHeader>
      </CollapsibleHeader>
      {opened &&
        links.map(({ title, path, sticker, subtitle }, i) => (
          <Fragment key={i}>
            <TitleAndSticker>
              <Link to={path}>{title}</Link>
              {sticker}
            </TitleAndSticker>
            {subtitle ? <Paragraph>{subtitle}</Paragraph> : <Spacer height={1} />}
          </Fragment>
        ))}
    </>
  );
};

interface QuickLinksProps {
  relatedAccounts: TransactionWithRelatedData['relatedAccounts'];
  relatedCases: TransactionWithRelatedData['relatedCases'];
  relatedInquiries: TransactionWithRelatedData['relatedInquiries'];
  relatedReports: TransactionWithRelatedData['relatedReports'];
  relatedTransactions: TransactionWithRelatedData['relatedTransactions'];
  relatedVerifications: TransactionWithRelatedData['relatedVerifications'];
}

const QuickLinks: VFC<QuickLinksProps> = ({
  relatedAccounts,
  relatedCases,
  relatedInquiries,
  relatedReports,
  relatedVerifications,
}) => {
  const possibleObjectTypes = (
    [
      [
        relatedAccounts,
        <QuickLinksForType
          key='account'
          type='Accounts'
          links={relatedAccounts.map(
            ({
              id,
              attributes: { 'name-full-localized': localized, 'name-full': nameFull },
              relationships: {
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                'account-type': { data: accountType },
              },
            }) => ({
              title:
                localized != null
                  ? `${id} (${localized})`
                  : nameFull != null
                    ? `${id} (${nameFull})`
                    : id,
              path: `/dashboard/accounts/${id}`,
              subtitle: accountType.attributes.name,
            }),
          )}
        />,
      ],
      [
        relatedCases,
        <QuickLinksForType
          key='case'
          type='Cases'
          links={relatedCases.map((o) => ({
            title: o.attributes.name,
            path: `/dashboard/cases/${o.id}`,
            sticker: <CaseStatusSticker caseState={o.attributes.state} />,
          }))}
        />,
      ],
      [
        relatedInquiries,
        <QuickLinksForType
          key='inquiry'
          type='Inquiries'
          links={relatedInquiries.map(
            ({
              id,
              attributes: { status, 'name-full-localized': localized, 'name-full': nameFull },
              relationships: { 'inquiry-template': inquiryTemplate, template },
            }) => ({
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              title: inquiryTemplate.data?.attributes.name ?? template.data.attributes.name,
              path: `/dashboard/inquiries/${id}`,
              sticker: <InquiryStatusSticker status={status} />,
              subtitle: localized ?? nameFull ?? 'No name collected',
            }),
          )}
        />,
      ],
      [
        relatedReports,
        <QuickLinksForType
          key='report'
          type='Reports'
          links={relatedReports.map((r) => ({
            title: reportTypeDisplay(r.type),
            path: `/dashboard/reports/${r.id}`,
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            sticker: <ReportStatusSticker report={r} />,
            ...(r.attributes['primary-input']
              ? {
                  subtitle: r.attributes['primary-input'],
                }
              : {}),
          }))}
        />,
      ],
      [
        relatedVerifications,
        <QuickLinksForType
          key='verification'
          type='Verifications'
          links={relatedVerifications.map((v) => ({
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            title: v.relationships['verification-template'].data.attributes.name,
            path: `/dashboard/verifications/${v.id}`,
            sticker: <VerificationStatusSticker verification={v} />,
            // subtitle will be filled with primary verification input
          }))}
        />,
      ],
    ] as const
  )
    .filter(([objectsForType]) => objectsForType.length > 0)
    .map(([_, elements]) => elements);

  return (
    <Card>
      <Card.Body>
        <Card.Title>Quick Links</Card.Title>
        <section>
          {intersperse(possibleObjectTypes, (i) => (
            <Spacer key={`separator-${i}`} height={1} />
          ))}
        </section>
      </Card.Body>
    </Card>
  );
};

export default QuickLinks;
